import React, { useState } from "react";
import { Link } from "react-router-dom";
import { UserPlus, Wallet, TrendingUp, ArrowRight, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { steps, testimonials } from "../mock";

const ICONS = { "user-plus": UserPlus, wallet: Wallet, "trending-up": TrendingUp };

export const StepsSection = () => {
  return (
    <section className="py-20 lg:py-28">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <div className="text-center mb-14">
          <h2 className="font-display text-[36px] lg:text-[48px] font-bold text-white mb-4">
            Start trading in 3 simple steps
          </h2>
          <p className="bb-text-mute text-[16px] max-w-lg mx-auto">
            Joining Northwind is fast and straightforward. Get started in minutes.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {steps.map((s, i) => {
            const Icon = ICONS[s.icon];
            return (
              <div
                key={s.id}
                className="bb-card bb-card-hover rounded-3xl p-8 relative group"
              >
                <div className="absolute top-6 right-6 text-[60px] font-display font-black text-white/[0.04] leading-none">
                  0{i + 1}
                </div>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1c3df5] to-[#0f1ea8] flex items-center justify-center mb-6 shadow-lg shadow-[#1c3df5]/30">
                  <Icon size={24} className="text-white" strokeWidth={1.8} />
                </div>
                <h3 className="font-display text-[22px] font-bold text-white mb-2">{s.title}</h3>
                <p className="bb-text-mute text-[14px] leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-14">
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/signup"
              data-testid="steps-open-account-cta"
              className="inline-flex items-center gap-2 px-6 py-3 bb-blue text-white font-medium rounded-lg transition-all hover:gap-3"
            >
              Open live account <ArrowRight size={16} />
            </Link>
            <Link
              to="/signup"
              data-testid="steps-demo-cta"
              className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 hover:border-white/40 text-white font-medium rounded-lg transition-all"
            >
              Free demo account
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export const Testimonials = () => {
  const [idx, setIdx] = useState(0);
  const t = testimonials[idx];
  const next = () => setIdx((i) => (i + 1) % testimonials.length);
  const prev = () => setIdx((i) => (i - 1 + testimonials.length) % testimonials.length);

  return (
    <section className="py-20 lg:py-28 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0c0f1c] to-transparent"></div>
      <div className="relative max-w-[1100px] mx-auto px-4 lg:px-8">
        <h2 className="font-display text-center text-[36px] lg:text-[48px] font-bold text-white mb-14">
          Why traders love Northwind
        </h2>

        <div className="bb-card rounded-3xl p-10 lg:p-14 relative">
          <div className="flex items-center gap-0.5 mb-6">
            {[...Array(t.rating)].map((_, i) => (
              <Star key={i} size={18} fill="#fbbf24" strokeWidth={0} className="text-[#fbbf24]" />
            ))}
          </div>
          <p className="text-white text-[20px] lg:text-[26px] leading-[1.5] font-display mb-6 min-h-[120px]">
            “{t.text}”
          </p>
          <div className="text-white/70 text-[14px]">— {t.author}</div>

          <div className="flex items-center justify-between mt-8">
            <div className="flex gap-1.5">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx ? "w-6 bg-[#1c3df5]" : "w-1.5 bg-white/20 hover:bg-white/40"
                  }`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={prev}
                className="w-10 h-10 rounded-full border border-white/15 hover:border-white/30 hover:bg-white/5 flex items-center justify-center text-white transition-all"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={next}
                className="w-10 h-10 rounded-full border border-white/15 hover:border-white/30 hover:bg-white/5 flex items-center justify-center text-white transition-all"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const MakeYourMoveBanner = () => {
  return (
    <section className="py-12 lg:py-16">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <div className="relative rounded-3xl overflow-hidden h-[320px] lg:h-[380px]">
          <img
            src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHw0fHwzRCUyMGNoYXJ0fGVufDB8fHx8MTc3ODg0ODIyNXww&ixlib=rb-4.1.0&q=85"
            alt="Make your move"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#07080d] via-[#07080d]/70 to-transparent"></div>
          <div className="relative z-10 h-full flex items-center px-8 lg:px-16 max-w-2xl">
            <div>
              <div className="font-display text-[36px] lg:text-[52px] leading-[1.05] font-bold text-white mb-6">
                Make<br />Your <span className="text-[#7d8eff]">Move.</span>
              </div>
              <Link
                to="/signup"
                data-testid="make-your-move-cta"
                className="inline-flex items-center gap-2 px-6 py-3 bb-blue text-white font-medium rounded-lg transition-all hover:gap-3"
              >
                Explore <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
